import {identity} from 'lodash';
import React, {forwardRef, PropsWithChildren, ReactNode, useRef, useState} from 'react';
import styled, {css} from 'styled-components';

import {KeyboardActionsEnum} from '../../../../libs/shared/core/src/helpers/browser/keyboardHelpers';
import {RendererOf} from '../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {Button} from '../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonChevronContent} from '../../../../libs/shared/ui/src/components/buttons/buttonChevronContent';
import {ButtonChrome} from '../../../../libs/shared/ui/src/components/buttons/buttonChrome';
import {ButtonVariantsEnum} from '../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {ButtonContent} from '../../../../libs/shared/ui/src/components/buttons/buttonContent';
import {ChromelessButton} from '../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {DropdownPlacement} from '../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {KeyboardEventHandlers} from '../../../../libs/shared/ui/src/components/keyboard/keyboardContext';
import {KeyboardShortcuts} from '../../../../libs/shared/ui/src/components/keyboard/keyboardShortcuts';
import {PopoverTransferProps} from '../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {TooltipCoordinator} from '../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {ellipsis} from '../../../../libs/shared/ui/src/styles/mixins';
import {PermissionDenied} from '../../../../srcCommon/permissions/permissionDenied';
import {PermissionResult} from '../../../helpers/permissionHelpers';
import {InboxesTopbarTooltip} from './inboxesTopbarTooltip';

/*
 * Props.
 */

export interface InboxesTopbarMainButtonChevronProps {
  renderDropdown: RendererOf<
    PopoverTransferProps & {
      placement: DropdownPlacement;
    }
  >;
  tooltipFirstLine: ReactNode;
  tooltipSecondLine?: ReactNode;
}

interface InboxesTopbarMainButtonProps {
  permissionResult: PermissionResult;
  onClick: () => void;
  keyboardAction: KeyboardActionsEnum;
  shouldHideKeyboardActionInTooltip?: boolean;
  tooltipFirstLine: ReactNode;
  tooltipSecondLine: ReactNode;
  icon: ReactNode;
  chevronProps?: InboxesTopbarMainButtonChevronProps;
  interactionId?: string;
}

/*
 * Style.
 */

const StyledMainActionWrapper = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: auto;
  grid-column-gap: 6px;
  align-items: center;
`;
const StyledIconWrapper = styled.div`
  margin-top: -1px;
  margin-bottom: -2px;
  margin-left: -2px;
`;

const StyledTextWrapper = styled.div`
  ${ellipsis()};
`;

const StyledChevronCoordinator = styled(TooltipCoordinator)`
  display: flex;
  flex-shrink: 0;
`;
const StyledChevronButton = styled(ChromelessButton)`
  display: flex;
`;
const commonButtonStyles = css`
  max-width: 286px;
`;
const StyledButtonChrome = styled(ButtonChrome)`
  ${commonButtonStyles};
`;
const StyledButton = styled(Button)`
  ${commonButtonStyles};
`;

/*
 * Component.
 */

export const InboxesTopbarMainButton = forwardRef<
  HTMLDivElement,
  PropsWithChildren<InboxesTopbarMainButtonProps>
>((props, ref) => {
  const [isChevronDropdownOpen, setIsChevronDropdownOpen] = useState(false);
  const chevronRef = useRef<HTMLDivElement>(null);

  const keyHandlers: KeyboardEventHandlers = {
    [props.keyboardAction]: props.onClick,
  };

  const renderTooltip = () => {
    if (props.permissionResult.isAllowed) {
      return (
        <InboxesTopbarTooltip
          keyboardAction={!props.shouldHideKeyboardActionInTooltip ? props.keyboardAction : undefined}
          firstLine={props.tooltipFirstLine}
          secondLine={props.tooltipSecondLine}
        />
      );
    }
    return <PermissionDenied reason={props.permissionResult.reason} />;
  };

  const maybeRenderDropdown = () => {
    if (!isChevronDropdownOpen || !props.chevronProps || !chevronRef.current) {
      return null;
    }

    return props.chevronProps?.renderDropdown({
      anchor: chevronRef,
      onRequestClose: () => setIsChevronDropdownOpen(false),
      wrap: identity,
      placement: 'bottom-end',
    });
  };

  if (props.chevronProps) {
    const renderChevronTooltip = () => {
      if (!props.permissionResult.isAllowed) {
        return <PermissionDenied reason={props.permissionResult.reason} />;
      }

      if (!props.chevronProps?.tooltipSecondLine) {
        return props.chevronProps?.tooltipFirstLine;
      }

      return (
        <InboxesTopbarTooltip
          keyboardAction={!props.shouldHideKeyboardActionInTooltip ? props.keyboardAction : undefined}
          firstLine={props.chevronProps?.tooltipFirstLine}
          secondLine={props.chevronProps?.tooltipSecondLine}
        />
      );
    };

    return (
      <>
        <StyledButtonChrome
          isDisabled={!props.permissionResult.isAllowed}
          variant={ButtonVariantsEnum.SECONDARY}
          size={VisualSizesEnum.MEDIUM}
          isRounded
          ref={ref}
        >
          {(buttonProps) => (
            <KeyboardShortcuts handlers={keyHandlers}>
              <TooltipCoordinator render={renderTooltip} isMultiline placement="bottom">
                <ChromelessButton
                  {...buttonProps}
                  onClick={props.onClick}
                  interactionId={props.interactionId}
                >
                  <ButtonContent {...buttonProps}>
                    <StyledMainActionWrapper>
                      <StyledIconWrapper>{props.icon}</StyledIconWrapper>
                      <StyledTextWrapper>{props.children}</StyledTextWrapper>
                    </StyledMainActionWrapper>
                  </ButtonContent>
                </ChromelessButton>
              </TooltipCoordinator>
              <StyledChevronCoordinator
                render={renderChevronTooltip}
                isMultiline={Boolean(props.chevronProps?.tooltipSecondLine)}
                placement="bottom-end"
              >
                <StyledChevronButton
                  {...buttonProps}
                  ref={chevronRef}
                  onClick={() => {
                    setIsChevronDropdownOpen(true);
                  }}
                  hasHover={isChevronDropdownOpen}
                  isOpen={isChevronDropdownOpen}
                >
                  <ButtonChevronContent {...buttonProps}>
                    <Icon name="chevronDownSmall" />
                  </ButtonChevronContent>
                </StyledChevronButton>
              </StyledChevronCoordinator>
            </KeyboardShortcuts>
          )}
        </StyledButtonChrome>
        {maybeRenderDropdown()}
      </>
    );
  }

  return (
    <KeyboardShortcuts handlers={keyHandlers}>
      <StyledButton
        ref={ref}
        isDisabled={!props.permissionResult.isAllowed}
        variant={ButtonVariantsEnum.SECONDARY}
        size={VisualSizesEnum.MEDIUM}
        onClick={props.onClick}
        renderTooltip={renderTooltip}
        isTooltipMultiline
      >
        <StyledMainActionWrapper>
          <StyledIconWrapper>{props.icon}</StyledIconWrapper>
          <StyledTextWrapper>{props.children}</StyledTextWrapper>
        </StyledMainActionWrapper>
      </StyledButton>
    </KeyboardShortcuts>
  );
});
